import { useMemo, useState } from "react";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Text } from "@astryxdesign/core/Text";
import { Badge } from "@astryxdesign/core/Badge";
import { Banner } from "@astryxdesign/core/Banner";
import { Divider } from "@astryxdesign/core/Divider";
import { Collapsible } from "@astryxdesign/core/Collapsible";
import { IconButton } from "@astryxdesign/core/IconButton";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { TextInput } from "@astryxdesign/core/TextInput";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { ChevronDown, ChevronRight, Folder, FolderOpen, HardDrive, Search } from "lucide-react";
import type { Camera, Device, Project } from "../../lib/api";
import type { HealthState } from "../health/useHealthEvents";
import { countOffline, describeReachability } from "../stream/reachability";
import { buildCameraTree } from "./cameraTree";

interface CameraPickerPanelProps {
  projects: Project[];
  devices: Device[];
  cameras: Camera[];
  /** Ordered ticked ids — the same array LiveGridPage renders the canvas
   *  from, so this panel and the canvas can never disagree about what's
   *  selected. */
  selection: readonly string[];
  onToggle: (cameraId: string) => void;
  /** The shared health feed, for the online/offline dot on each camera and the
   *  offline count on each recorder. Required, not optional: this panel is
   *  mounted twice (desktop side panel and mobile sheet), and an optional prop
   *  is how one of the two ends up quietly without it. */
  health: HealthState;
  /** The cap this browser's picker enforces (streamSlots' limit — the "max
   *  streaming var"). An unticked camera disables once selection.length
   *  reaches it; a ticked one always stays untickable regardless. */
  limit: number;
  /** Seeds which project starts expanded — the /live/:projectCode route's
   *  role now that the tree is global: an initial filter an operator lands
   *  on, not a hard scope. Every other project stays browsable right
   *  alongside it. */
  initialExpandedProjectId?: string | null;
}

function toggleMember(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * The live wall's camera picker: a project → recorder → camera tree of
 * checkboxes, plus a "Selected" section so a camera ticked under one project
 * stays reachable (and untickable) after navigating to another — without it,
 * ticking across projects would leave no way back to a camera that scrolled
 * out of the current filter (see the plan's own note on why this section
 * exists).
 *
 * Laid out VSCode-sidebar-style: the tree is the primary, scrollable region
 * (`flex: 1`, growing to fill whatever height the caller gives this
 * component), with Selected docked below it as its own collapsible section —
 * closer at hand than scrolling to the very bottom of a long tree, and
 * out of the way (collapsed) when it isn't needed. `Collapsible` supplies
 * its own chevron for the trigger; see CameraAccessEditor.tsx for the same
 * label+Badge trigger shape this reuses.
 *
 * The tree itself is rendered as flat rows with a chevron IconButton per
 * project/recorder, matching CamerasPage's convention, rather than nested
 * Collapsibles — dense hierarchical data is rows here, not accordions (see
 * this repo's own CLAUDE.md on Table/List vs. Card).
 */
export function CameraPickerPanel({ projects, devices, cameras, selection, onToggle, health, limit, initialExpandedProjectId }: CameraPickerPanelProps) {
  const [expandedProjectIds, setExpandedProjectIds] = useState<ReadonlySet<string>>(
    () => new Set(initialExpandedProjectId ? [initialExpandedProjectId] : []),
  );
  const [expandedDeviceIds, setExpandedDeviceIds] = useState<ReadonlySet<string>>(() => new Set());
  const [search, setSearch] = useState("");

  const selected = useMemo(() => new Set(selection), [selection]);
  const byId = useMemo(() => new Map(cameras.map((c) => [c.id, c] as const)), [cameras]);

  // Client-side, over data that's already fully loaded — same reasoning as
  // CamerasPage's own search. Filtering happens on the SOURCE arrays before
  // buildCameraTree runs, not on its output: a camera that matches only
  // through its project's or recorder's name still needs that project/
  // recorder in the filtered lists, or buildCameraTree's own grouping would
  // drop it as belonging to a folder that "has no matches".
  const searchLower = search.trim().toLowerCase();
  const isSearching = searchLower.length > 0;

  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p] as const)), [projects]);
  const deviceById = useMemo(() => new Map(devices.map((d) => [d.id, d] as const)), [devices]);

  const cameraMatches = (c: Camera) => c.name.toLowerCase().includes(searchLower) || c.location.toLowerCase().includes(searchLower);
  const deviceMatches = (d: Device) => d.name.toLowerCase().includes(searchLower);
  const projectMatches = (p: Project) => p.name.toLowerCase().includes(searchLower);

  const filteredCameras = useMemo(() => {
    if (!isSearching) return cameras;
    return cameras.filter((c) => {
      const device = c.device_id ? deviceById.get(c.device_id) : undefined;
      const project = projectById.get(c.project_id);
      return cameraMatches(c) || (device && deviceMatches(device)) || (project && projectMatches(project));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cameraMatches/deviceMatches/projectMatches close over `search`, already a dep via searchLower
  }, [cameras, deviceById, projectById, isSearching, searchLower]);

  const filteredDevices = useMemo(() => {
    if (!isSearching) return devices;
    return devices.filter((d) => deviceMatches(d) || filteredCameras.some((c) => c.device_id === d.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, filteredCameras, isSearching, searchLower]);

  // A match inside a collapsed project or recorder must still surface — an
  // operator searching for one camera should not also have to guess which
  // folders to open first. Search overrides the persisted expand state
  // entirely rather than merging with it, and reverts the moment the search
  // box empties (matching CamerasPage's own effectiveExpandedIds).
  const effectiveExpandedProjectIds = isSearching ? new Set(filteredCameras.map((c) => c.project_id)) : expandedProjectIds;
  const effectiveExpandedDeviceIds = isSearching ? new Set(filteredDevices.map((d) => d.id)) : expandedDeviceIds;

  const rows = useMemo(
    () => buildCameraTree(projects, filteredDevices, filteredCameras, selected, effectiveExpandedProjectIds, effectiveExpandedDeviceIds),
    [projects, filteredDevices, filteredCameras, selected, effectiveExpandedProjectIds, effectiveExpandedDeviceIds],
  );

  const isAtCap = selection.length >= limit;
  const selectedCameras = useMemo(() => selection.map((id) => byId.get(id)).filter((c): c is Camera => c !== undefined), [selection, byId]);

  return (
    <VStack gap={2} style={{ flex: 1, minHeight: 0 }}>
      {isAtCap && (
        <Banner
          status="info"
          title={`${selection.length} of ${limit} selected`}
          description="Untick a camera below to add a different one."
          collapsible={false}
        />
      )}

      {cameras.length > 0 && (
        <TextInput
          label="Search cameras"
          isLabelHidden
          placeholder="Search by camera, recorder, or project name"
          value={search}
          onChange={setSearch}
          startIcon={<Search size={16} />}
          hasClear
        />
      )}

      {/* The primary, scrollable region — grows to fill whatever space is
          left after the banner/search above and the docked Selected section
          below, so a long tree scrolls in place instead of pushing Selected
          off the bottom of the panel. */}
      <VStack gap={0} style={{ flex: 1, minHeight: 0 }} isScrollable>
        {cameras.length === 0 ? (
          <Text type="supporting" color="secondary">
            No cameras yet.
          </Text>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Search size={32} />} title="No matching cameras" description={`No camera, recorder, or project matches "${search}".`} />
        ) : (
          rows.map((row) => {
            if (row.kind === "project") {
              // The effective set, not the raw persisted one — while
              // searching, a project can be force-expanded (its matching
              // cameras are already rendered below, from the same set) and
              // the chevron must agree with what's actually on screen.
              const isExpanded = effectiveExpandedProjectIds.has(row.id);
              return (
                <HStack key={`project:${row.id}`} gap={1} vAlign="center" padding={1}>
                  <IconButton
                    label={isExpanded ? `Collapse ${row.project.name}` : `Expand ${row.project.name}`}
                    icon={isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedProjectIds((prev) => toggleMember(prev, row.id))}
                  />
                  {isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />}
                  <Text weight="medium" style={{ flex: 1 }}>
                    {row.project.name}
                  </Text>
                  <Badge variant="neutral" label={`${row.selectedCount}/${row.cameraCount}`} />
                </HStack>
              );
            }

            if (row.kind === "device") {
              return (
                <DeviceRow
                  key={`device:${row.id}`}
                  device={row.device}
                  cameras={row.cameras}
                  selectedCount={row.selectedCount}
                  health={health}
                  isExpanded={effectiveExpandedDeviceIds.has(row.id)}
                  onToggleExpanded={() => setExpandedDeviceIds((prev) => toggleMember(prev, row.id))}
                />
              );
            }

            return (
              <CameraRow
                key={`camera:${row.id}`}
                camera={row.camera}
                isSelected={selected.has(row.camera.id)}
                isAtCap={isAtCap}
                limit={limit}
                health={health}
                paddingInlineStart={row.isChild ? 6 : 3}
                onToggle={onToggle}
              />
            );
          })
        )}
      </VStack>

      <Divider />

      {/* Docked at the bottom, its own collapsible section — always
          present (even at zero) so it reads as a fixed part of the sidebar's
          structure, the same way a VSCode panel's bottom sections stay put
          whether or not they currently hold anything. */}
      <Collapsible
        defaultIsOpen
        trigger={
          <HStack gap={1.5} vAlign="center">
            <Text type="label" color="secondary">
              Selected
            </Text>
            <Badge variant="neutral" label={String(selectedCameras.length)} />
          </HStack>
        }
      >
        <VStack gap={0}>
          {selectedCameras.length === 0 ? (
            <Text type="supporting" color="secondary">
              Nothing selected yet.
            </Text>
          ) : (
            selectedCameras.map((camera) => (
              <CameraRow
                key={camera.id}
                camera={camera}
                isSelected
                isAtCap={isAtCap}
                limit={limit}
                health={health}
                paddingInlineStart={1}
                onToggle={onToggle}
              />
            ))
          )}
        </VStack>
      </Collapsible>
    </VStack>
  );
}

interface DeviceRowProps {
  device: Device;
  cameras: Camera[];
  selectedCount: number;
  health: HealthState;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

function DeviceRow({ device, cameras, selectedCount, health, isExpanded, onToggleExpanded }: DeviceRowProps) {
  const cameraCount = cameras.length;
  // Shown on the recorder row so a dead site is visible without expanding it.
  const offlineCount = countOffline(cameras, health.byCamera, health.phase === "live");

  return (
    <HStack gap={1} vAlign="center" padding={1} paddingInlineStart={3}>
      <IconButton
        label={isExpanded ? `Collapse ${device.name}` : `Expand ${device.name}`}
        icon={isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        variant="ghost"
        size="sm"
        isDisabled={cameraCount === 0}
        onClick={onToggleExpanded}
      />
      <HardDrive size={16} />
      <Text style={{ flex: 1 }}>{device.name}</Text>
      {offlineCount > 0 && <Badge variant="error" label={`${offlineCount} offline`} />}
      <Badge variant="neutral" label={`${selectedCount}/${cameraCount}`} />
    </HStack>
  );
}

/** The indents a camera row is drawn at: the Selected list, a standalone
 *  camera under its project, and a channel under its recorder. */
type RowIndent = 1 | 3 | 6;

interface CameraRowProps {
  camera: Camera;
  isSelected: boolean;
  isAtCap: boolean;
  limit: number;
  health: HealthState;
  paddingInlineStart: RowIndent;
  onToggle: (cameraId: string) => void;
}

/** One camera: its reachability dot and its checkbox. Shared by the tree and
 *  the Selected list so the two can never show different things for the same
 *  camera. */
function CameraRow({ camera, isSelected, isAtCap, limit, health, paddingInlineStart, onToggle }: CameraRowProps) {
  const reachability = describeReachability(health.byCamera.get(camera.id), health.phase === "live");
  const isDisabled = !isSelected && (!camera.enabled || isAtCap);
  const disabledMessage = !camera.enabled ? "This camera is disabled" : isAtCap ? `Wall is full — ${limit} max` : undefined;

  return (
    <HStack gap={1.5} vAlign="center" padding={1} paddingInlineStart={paddingInlineStart}>
      {/* The camera's name is in the label so a screen reader reading a column
          of dots knows which camera each one belongs to. */}
      <StatusDot variant={reachability.variant} label={`${camera.name}: ${reachability.label}`} tooltip={reachability.tooltip} />
      <CheckboxInput
        label={camera.name}
        size="sm"
        value={isSelected}
        isDisabled={isDisabled}
        disabledMessage={disabledMessage}
        onChange={() => onToggle(camera.id)}
      />
    </HStack>
  );
}
